import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { apiRequest } from '../services/apiInterceptor';
import sessionValidator from '../utils/sessionValidator';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Public paths that should not redirect to login
  const publicPaths = ['/', '/login', '/create-account', '/confirm-registration', '/verify-email', '/reset-password', '/pricing', '/unsubscribe'];
  const isPublicPath = publicPaths.some(path => location.pathname === path || location.pathname.startsWith('/reset-password/'));

  // Check authentication status on mount.
  //
  // Auth lives in an HttpOnly cookie that JS cannot read, so localStorage may
  // be empty even when we are logged in (e.g. right after the OAuth server
  // redirect). We therefore always probe /api/useraccount — the cookie is
  // attached by the apiInterceptor — and treat the API's answer as the source
  // of truth. Cached user data only seeds an optimistic UI while the probe
  // runs.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const cachedUser = authService.getUser();
        if (cachedUser) {
          setUser(cachedUser);
          setIsAuthenticated(true);
        }

        let serverUser = null;
        try {
          serverUser = await apiRequest('/api/useraccount');
        } catch (probeError) {
          const status = probeError?.status;
          if (status === 401 || status === 403) {
            // Confirmed unauthenticated — fall through to the cleanup branch.
            serverUser = null;
          } else {
            // Network / server hiccup. Keep whatever cached state we have and
            // let the next request retry; do not bounce the user to /login.
            return;
          }
        }

        if (serverUser) {
          const rememberMe = authService.isRememberMeEnabled();
          authService.setUser(serverUser, rememberMe);
          setUser(serverUser);
          setIsAuthenticated(true);

          if (!sessionValidator.isValidationActive()) {
            sessionValidator.startPeriodicValidation(() => {
              handleSessionInvalid();
            });
          }
        } else {
          authService.clearAuth();
          setIsAuthenticated(false);
          setUser(null);
          if (!isPublicPath) {
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('AuthContext: Auth check error:', error);
        authService.clearAuth();
        setIsAuthenticated(false);
        setUser(null);
        if (!isPublicPath) {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate, isPublicPath]);

  // Cleanup session validator on unmount
  useEffect(() => {
    return () => {
      sessionValidator.stopPeriodicValidation();
    };
  }, []);

  // Handle session invalidation
  const handleSessionInvalid = async () => {
    try {
      // Stop periodic validation
      sessionValidator.stopPeriodicValidation();
      
      // Clear authentication data
      authService.clearAuth();
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Error handling session invalidation:', error);
    }
  };

  // Login function
  // When isNewUser is true (e.g. after email verification), the free package is
  // assigned immediately so it doesn't rely on a localStorage flag that could
  // interfere with future Stripe upgrades.
  const login = async (email, password, options = {}) => {
    // Support legacy callers that pass a plain boolean (rememberMe)
    const opts = typeof options === 'boolean' ? { rememberMe: options } : options;
    const { rememberMe = false, isNewUser = false } = opts;
    try {
      setIsLoading(true);
      setAuthError(null);
      
      const response = await authService.login(email, password, rememberMe);
      
      // Server indicates the account is pending email verification — surface this
      // so the Login page can route the user to /verify-email instead of treating
      // it as a generic auth failure.
      if (response && response.result === 'UserPending') {
        const pendingError = new Error(response.message || 'Your account is pending activation.');
        pendingError.code = 'UserPending';
        pendingError.email = email;
        throw pendingError;
      }

      if (response && response.user) {
        // Assign free package for brand-new users before setting state / navigating.
        // Check for existing subscriptions first to avoid overwriting a paid plan
        // (oauth_is_signup is set on every Google login, not just first-time).
        if (isNewUser) {
          try {
            console.log('[AuthContext] New user detected – checking for existing subscriptions');
            const { apiRequest } = await import('../services/apiInterceptor');
            const existingSubs = await apiRequest('/api/usersubscription');
            if (!existingSubs || existingSubs.length === 0) {
              console.log('[AuthContext] No existing subscriptions – assigning free package (rateId 16)');
              await apiRequest('/api/usersubscription/subscribe', {
                method: 'POST',
                body: JSON.stringify({ rateId: 16, paymentCycle: 'Monthly' }),
              });
              console.log('[AuthContext] Free package assigned successfully');
            } else {
              console.log('[AuthContext] User already has subscriptions – skipping free package assignment');
            }
          } catch (subErr) {
            console.warn('[AuthContext] Free package check/assignment failed (non-blocking):', subErr);
          }
        }

        setUser(response.user);
        setIsAuthenticated(true);
        
        // Start periodic session validation after successful login (only if not already running)
        if (!sessionValidator.isValidationActive()) {
          sessionValidator.startPeriodicValidation(() => {
            // console.log('Periodic session validation failed during app usage, logging out user');
            handleSessionInvalid();
          });
        } else {
          // console.log('AuthContext: Periodic validation already active after login, skipping start');
        }
        
        // Navigate to dashboard on successful login
        navigate('/dashboard');
        
        return response;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message || 'Login failed. Please check your credentials.');
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      
      // Stop periodic session validation
      sessionValidator.stopPeriodicValidation();
      
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state regardless of API call success
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
      setIsLoading(false);
      
      // Navigate to login page
      navigate('/');
    }
  };

  // Clear auth error
  const clearError = () => {
    setAuthError(null);
  };
  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
    // Use the current remember me preference, defaulting to true for better UX
    const rememberMe = authService.isRememberMeEnabled();
    authService.setUser(userData, rememberMe);
  };

  // Get user sessions
  const getSessions = async () => {
    try {
      return await authService.getSessions();
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error;
    }
  };
  // Terminate session (alias for kickSession for backward compatibility)
  const terminateSession = async (sessionId) => {
    try {
      return await authService.endSession(sessionId);
    } catch (error) {
      console.error('Terminate session error:', error);
      throw error;
    }
  };

  // Kick session (legacy name)
  const kickSession = async (sessionId) => {
    try {
      return await authService.endSession(sessionId);
    } catch (error) {
      console.error('Kick session error:', error);
      throw error;
    }
  };

  // Terminate all other sessions
  const terminateAllOtherSessions = async () => {
    try {
      const sessions = await authService.getSessions();
      const currentSessionData = authService.getSessionData();
      const currentSessionId = currentSessionData?.sessionId;
      
      // Filter out the current session
      const otherSessions = sessions.filter(session => session.id !== currentSessionId);
      
      // End all other sessions
      const promises = otherSessions.map(session => authService.endSession(session.id));
      await Promise.all(promises);
      
      return otherSessions;
    } catch (error) {
      console.error('Terminate all sessions error:', error);
      throw error;
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  // Validate current session
  const validateSession = async () => {
    try {
      return await authService.validateSession();
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    authError,
    login,
    logout,
    clearError,
    updateUser,
    updateProfile,
    getSessions,
    kickSession,
    terminateSession,
    terminateAllOtherSessions,
    validateSession,
    // Auth lives in an HttpOnly cookie now — JS cannot read the JWT. Kept as a
    // null stub so any remaining callers don't crash.
    getToken: () => null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
